import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMapMarkerAlt,
  faChevronDown,
  faChevronUp,
  faBuilding,
  faLock,
  faSignOutAlt,
} from "@fortawesome/free-solid-svg-icons";
import "./PageHeader.css";

export default function PageHeader({
  title,
  subtitle,
  icon,
  activeLocation,
  locations = [],
  onChangeLocation,
  companyDisplayName,
  currentUser,
  onLogout,
  onLock,
  showControls = false,
  bannerActions = null,
}) {
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [isUserOpen, setIsUserOpen] = useState(false);
  const locationRef = useRef(null);
  const userRef = useRef(null);
  const activeLocId = activeLocation?.id || activeLocation?._id;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isLocationOpen && locationRef.current && !locationRef.current.contains(e.target)) {
        setIsLocationOpen(false);
      }
      if (isUserOpen && userRef.current && !userRef.current.contains(e.target)) {
        setIsUserOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isLocationOpen, isUserOpen]);

  return (
    <div className="page-header-banner">
      <div className="banner-flex">
        <div className="banner-left">
          <div className="banner-icon">{icon}</div>
          <div className="banner-copy">
            <h1>{title}</h1>
            {subtitle && <p>{subtitle}</p>}
          </div>
        </div>

        {bannerActions ? <div className="banner-actions-slot">{bannerActions}</div> : null}

        {showControls && (
          <div className="banner-right" ref={locationRef}>
            <div className="banner-actions">
              <button className="icon-btn ghost" onClick={onLock}>
                <FontAwesomeIcon icon={faLock} />
              </button>

              <div
                className={`location-switcher on-banner ${isLocationOpen ? "open" : ""}`}
                onClick={() => setIsLocationOpen((v) => !v)}
              >
                <FontAwesomeIcon icon={faMapMarkerAlt} className="location-pin-icon" />
                <span className="current-location-name">
                  {activeLocation?.name || "Select Location"}
                </span>
                <FontAwesomeIcon
                  icon={isLocationOpen ? faChevronUp : faChevronDown}
                  className="location-dropdown-arrow-icon"
                />
                {isLocationOpen && (
                  <div
                    className="location-dropdown-menu on-banner-menu"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="location-dropdown-company-header">
                      {companyDisplayName || "Locations"}
                    </div>
                    {locations.length > 0 && <hr className="location-divider" />}
                    {locations.map((loc) => {
                      const locId = loc.id || loc._id;
                      return (
                        <div
                          key={locId || loc.name}
                          className="location-dropdown-item"
                          onClick={(e) => {
                            e.stopPropagation();
                            onChangeLocation?.(loc);
                            setIsLocationOpen(false);
                          }}
                        >
                          <FontAwesomeIcon icon={faBuilding} className="location-item-icon" />
                          <div className="location-item-details">
                            <span className="location-item-name">
                              {loc.name}{" "}
                              {locId && locId === activeLocId && (
                                <span className="current-tag">Current</span>
                              )}
                            </span>
                            <span className="location-item-address">
                              {[loc.address, loc.city, loc.state, loc.zip].filter(Boolean).join(", ")}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="banner-user" ref={userRef} onClick={() => setIsUserOpen((v) => !v)}>
                {(currentUser?.name || "User")
                  .split(" ")
                  .map((n) => n[0])
                  .join("")
                  .toUpperCase()}
                {isUserOpen && (
                  <div className="user-dropdown-menu on-banner-user" onClick={(e) => e.stopPropagation()}>
                    <div className="user-info-section">
                      <div className="user-name">{currentUser?.name || "User"}</div>
                      <div className="user-email">{currentUser?.email || ""}</div>
                      <div className="user-role">{currentUser?.role?.toUpperCase() || "USER"}</div>
                    </div>
                    <div
                      className="logout-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onLogout?.();
                        setIsUserOpen(false);
                      }}
                    >
                      <FontAwesomeIcon icon={faSignOutAlt} className="logout-icon" /> Logout
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
